import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { faBank } from '@fortawesome/pro-solid-svg-icons'
import {
  Alert,
  Breadcrumb,
  Card,
  DatePicker,
  Form,
  Heading,
  HeadingVariants,
  Icon,
  Popup,
  RadioGroupItem,
  Text,
} from '@/ui'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UploadDocumentType } from '@/constants/businessConstants'

import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { PracticeProfileFormCta } from '../PracticeProfileFormCta'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { UploadItem } from '../shared/UploadItem'
import {
  FILING_STATUSES,
  FilingStatusOptions,
  TAX_ENTITY_TYPES,
  TaxEntityTypeOptions,
} from '@/features/taxes/taxConstants'

// TODO: Replace Formik with New UI
import { getFieldName } from '@/base'

export const shouldDisplaySpouse = (
  filingStatus: FILING_STATUSES | null | undefined
) => Boolean(filingStatus && ['married_filing_jointly'].includes(filingStatus))

export const TaxInformationForm = () => {
  const taxYear = DateTime.now().year
  const [year, setYear] = useState(taxYear.toString())
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const fp = useReselector(getFinancialProfile)
  const nextTaxYear = (Number(currentTaxYear) + 1).toString()

  const taxDocCat = useReselector(
    selectUserDocumentCategoryByInternalName,
    UserDocumentCategoryIdentifier.finalReturn
  )

  const sentCategory = useReselector(selectUserDocumentCategory, taxDocCat?.id)

  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      filingStatus: fp?.filingStatus,
      taxEntityType: fp?.taxEntityType,
      itemizedDeduction: fp?.itemizedDeduction ? 'true' : 'false',
      numberOfDependents: fp?.numberOfDependents,
      wIncomeInCents: fp?.wIncomeInCents && centsToDollars(fp?.wIncomeInCents),
      scorpPracticeWIncomeInCents:
        fp?.scorpPracticeWIncomeInCents &&
        centsToDollars(fp?.scorpPracticeWIncomeInCents),
      federalWithholdingInCents:
        fp?.federalWithholdingInCents &&
        centsToDollars(fp?.federalWithholdingInCents),
      stateWithholdingInCents:
        fp?.stateWithholdingInCents &&
        centsToDollars(fp?.stateWithholdingInCents),
      otherIndividualIncomeInCents:
        fp?.otherIndividualIncomeInCents &&
        centsToDollars(fp?.otherIndividualIncomeInCents),
      spouseFirstName: fp?.spouseFirstName,
      spouseLastName: fp?.spouseLastName,
      spouseWIncomeInCents:
        fp?.spouseWIncomeInCents && centsToDollars(fp?.spouseWIncomeInCents),
      spouseFederalWithholdingInCents:
        fp?.spouseFederalWithholdingInCents &&
        centsToDollars(fp?.spouseFederalWithholdingInCents),
      spouseStateWithholdingInCents:
        fp?.spouseStateWithholdingInCents &&
        centsToDollars(fp?.spouseStateWithholdingInCents),
      spouseIndividualIncomeInCents:
        fp?.spouseIndividualIncomeInCents &&
        centsToDollars(fp?.spouseIndividualIncomeInCents),
    },

    onSubmit: async ({
      filingStatus,
      taxEntityType,
      itemizedDeduction,
      numberOfDependents,
      wIncomeInCents,
      scorpPracticeWIncomeInCents,
      federalWithholdingInCents,
      stateWithholdingInCents,
      otherIndividualIncomeInCents,
      spouseFirstName,
      spouseLastName,
      spouseWIncomeInCents,
      spouseFederalWithholdingInCents,
      spouseStateWithholdingInCents,
      spouseIndividualIncomeInCents,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          filingStatus,
          taxEntityType,
          itemizedDeduction: itemizedDeduction === 'true' ? true : false,
          numberOfDependents,
          wIncomeInCents: dollarsToCents(wIncomeInCents),
          scorpPracticeWIncomeInCents: dollarsToCents(
            scorpPracticeWIncomeInCents
          ),
          federalWithholdingInCents: dollarsToCents(federalWithholdingInCents),
          stateWithholdingInCents: dollarsToCents(stateWithholdingInCents),
          otherIndividualIncomeInCents: dollarsToCents(
            otherIndividualIncomeInCents
          ),
          spouseFirstName,
          spouseLastName,
          spouseWIncomeInCents: dollarsToCents(spouseWIncomeInCents),
          spouseFederalWithholdingInCents: dollarsToCents(
            spouseFederalWithholdingInCents
          ),
          spouseStateWithholdingInCents: dollarsToCents(
            spouseStateWithholdingInCents
          ),
          spouseIndividualIncomeInCents: dollarsToCents(
            spouseIndividualIncomeInCents
          ),
        })
      )

      if (res) {
        navigate('/practice/profile')
      }
    },
  })

  const { values, submitForm, isSubmitting, isValid } = formik
  const displaySpouse = shouldDisplaySpouse(values.filingStatus)
  const isScorp = values.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

  // TODO: Replace Form with New UI
  return (
    <div className="flex flex-col gap-2 px-4">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate('/practice/profile')
            }}
          >
            <Icon icon={faArrowLeft} style={{ marginRight: 8 }} />
            Back to Practice Profile
          </Breadcrumb.Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex flex-row gap-2 items-center mt-4">
        <Icon
          icon={faBank}
          backgroundStyle="rounded"
          backgroundColor="priority2"
          size="sm"
        />

        <Heading variant={HeadingVariants.H2}>
          {taxYear} Tax Information
        </Heading>
      </div>

      <Card>
        <FormikProvider value={formik}>
          <div className="flex flex-col space-y-4 gap-4">
            {error && <Alert variant="error" description={error.message} />}

            <Form.Select
              name={getFieldName<typeof values>('filingStatus')}
              label="Filing Status"
              options={FilingStatusOptions}
            />

            <Form.Select
              name={getFieldName<typeof values>('taxEntityType')}
              label="Tax Entity Type"
              className="w-full"
              options={TaxEntityTypeOptions}
            />

            <Form.RadioGroup
              name={getFieldName<typeof values>('itemizedDeduction')}
              label="Deduction Type"
            >
              <div className="flex flex-col gap-2">
                {[false, true].map((option) => (
                  <RadioGroupItem
                    key={option.toString()}
                    value={option.toString()}
                    label={option ? 'Itemized' : 'Standard'}
                  />
                ))}
              </div>
            </Form.RadioGroup>

            <Form.Input
              name={getFieldName<typeof values>('numberOfDependents')}
              label="Number of Dependents"
            />

            {isScorp && (
              <Form.Input
                name={getFieldName<typeof values>(
                  'scorpPracticeWIncomeInCents'
                )}
                label="YTD W-2 Private Practice"
                componentType="currency"
              />
            )}

            <Form.Input
              name={getFieldName<typeof values>('wIncomeInCents')}
              label="YTD W-2 Income"
              componentType="currency"
            />

            <Form.Input
              name={getFieldName<typeof values>('federalWithholdingInCents')}
              label="YTD Federal Withholding"
              componentType="currency"
            />

            <Form.Input
              name={getFieldName<typeof values>('stateWithholdingInCents')}
              label="YTD State Withholding"
              componentType="currency"
            />

            <Form.Input
              name={getFieldName<typeof values>('otherIndividualIncomeInCents')}
              label="YTD 1099 Income"
              componentType="currency"
            />

            {displaySpouse && (
              <>
                <Form.Input
                  name={getFieldName<typeof values>('spouseFirstName')}
                  label="Spouse First Name"
                />

                <Form.Input
                  name={getFieldName<typeof values>('spouseLastName')}
                  label="Spouse Last Name"
                />

                <Form.Input
                  name={getFieldName<typeof values>('spouseWIncomeInCents')}
                  label="Spouse YTD W-2 Income"
                  componentType="currency"
                />

                <Form.Input
                  name={getFieldName<typeof values>(
                    'spouseFederalWithholdingInCents'
                  )}
                  label="Spouse YTD Federal Withholding"
                  componentType="currency"
                />

                <Form.Input
                  name={getFieldName<typeof values>(
                    'spouseStateWithholdingInCents'
                  )}
                  label="Spouse YTD State Withholding"
                  componentType="currency"
                />

                <Form.Input
                  name={getFieldName<typeof values>(
                    'spouseIndividualIncomeInCents'
                  )}
                  label="Spouse YTD 1099"
                  componentType="currency"
                />
              </>
            )}

            <UploadItem
              label="Prior Year Tax Return"
              documentCategory={UserDocumentCategoryIdentifier.finalReturn}
              uploadProps={{ year: year.toString() }}
              additionalTrackingProperties={{
                document_category: UploadDocumentType.TAX,
                document_name: sentCategory?.title || '',
                tax_year: Number(year),
              }}
            >
              <Text>This is your Federal and/or State Tax Return(s).</Text>

              <DatePicker
                placeholder="Select year"
                label="Tax Year"
                value={year?.toString() ?? ''}
                maxDate={new Date()}
                onChange={setYear}
                dateFormat={DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT}
                showYearPicker
                clearable
                afterLabel={
                  <Popup
                    content={
                      <Text>
                        A tax year is the 12-month calendar year covered by a
                        tax return. In the U.S., the tax year for individuals
                        runs from Jan. 1 to Dec. 31 and includes taxes owed on
                        earnings during that period. For example, taxes withheld
                        or owed for earnings during the calendar year{' '}
                        {currentTaxYear} would be included on the tax return
                        sent to the IRS in {nextTaxYear}. Even though you are
                        sending the tax return in {nextTaxYear}, it is for the
                        tax year {currentTaxYear}.
                      </Text>
                    }
                  />
                }
              />
            </UploadItem>

            <PracticeProfileFormCta
              submitForm={submitForm}
              isSubmitting={isSubmitting}
              isValid={isValid}
            />
          </div>
        </FormikProvider>
      </Card>
    </div>
  )
}
